import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { cn } from '@lombok/ui';
import { IconMoreVertical } from '@/components/icons';

export interface RowAction {
  label: string;
  /** Runs when chosen. Use `to` instead for a link to another screen. */
  onSelect?: () => void;
  to?: string;
  /** Destructive action: red text, and a divider above it. */
  tone?: 'danger';
  disabled?: boolean;
}

const MENU_WIDTH = 192;
const GAP = 4;
const EDGE = 8;

/**
 * "Acciones" cell for list rows: one kebab button that opens the row's actions, instead of a row
 * of buttons that eats the width. The menu is drawn in a portal with fixed coordinates, because
 * tables sit inside `overflow-hidden/auto` wrappers that would clip it; it opens below the
 * button (above when there is no room), closes on a click outside, Escape, scroll or resize, and
 * moves with the arrow keys.
 */
export function RowActionsMenu({ actions, label = 'Acciones' }: { actions: RowAction[]; label?: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Place the menu once it is measured: right-aligned with the button, flipped upward if it would
  // run off the bottom of the window.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !menuRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const height = menuRef.current.offsetHeight;
    const left = Math.max(EDGE, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - EDGE));
    const below = rect.bottom + GAP;
    const top = below + height > window.innerHeight - EDGE ? Math.max(EDGE, rect.top - GAP - height) : below;
    setPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
    const close = () => setOpen(false);
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !buttonRef.current?.contains(target)) close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        buttonRef.current?.focus();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const items = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
        const at = items.indexOf(document.activeElement as HTMLButtonElement);
        const next = e.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
        items[next]?.focus();
      } else if (e.key === 'Tab') {
        close();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = () => {
    setPos(null);
    setOpen((v) => !v);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-muted hover:text-text-primary"
      >
        <IconMoreVertical />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, width: MENU_WIDTH, visibility: pos ? 'visible' : 'hidden' }}
              className="fixed z-50 overflow-hidden rounded-lg border border-border bg-surface py-1 shadow-lg"
            >
              {actions.map((action) => (
                <div key={action.label} className={cn(action.tone === 'danger' && 'mt-1 border-t border-border pt-1')}>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={action.disabled}
                    onClick={() => {
                      setOpen(false);
                      if (action.to) navigate(action.to);
                      else action.onSelect?.();
                    }}
                    className={cn(
                      'flex min-h-10 w-full items-center px-4 text-left text-sm font-medium transition-colors hover:bg-surface-muted focus-visible:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-50',
                      action.tone === 'danger' ? 'text-danger' : 'text-text-primary',
                    )}
                  >
                    {action.label}
                  </button>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
